import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Check, Car, Users, Briefcase, Calendar as CalendarIcon, Clock, ArrowRight, MapPin, Copy, NavigationArrow, AirplaneTakeoff, Train, CastleTurret, MapTrifold, Timer, SignOut, Plus, CurrencyEur, DownloadSimple, PencilSimple, FloppyDisk, CreditCard, Money, ChatText, Warning } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useConnectAuth } from "./ConnectAuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API}/api`;

const getToken = () => localStorage.getItem("altura_connect_token");
const ax = axios.create({ baseURL: `${API}/api/connect` });
ax.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Address Autocomplete (same as public site) ───
const AddressAutocomplete = ({ value, onChange, onPlaceSelect, placeholder, required, testId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const fetchSuggestions = async (input) => {
    if (input.length < 3) { setSuggestions([]); setShowDropdown(false); return; }
    setLoading(true);
    try {
      const resp = await axios.get(`${API_BASE}/places/autocomplete`, { params: { input } });
      const items = resp.data.suggestions || [];
      setSuggestions(items);
      setShowDropdown(items.length > 0);
    } catch (err) { console.error("Autocomplete fetch error:", err); setSuggestions([]); }
    finally { setLoading(false); }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = async (suggestion) => {
    setShowDropdown(false);
    const pred = suggestion.placePrediction;
    const placeId = pred.placeId;
    const displayText = pred?.structuredFormat?.mainText?.text || pred?.text?.text || "";
    try {
      const resp = await axios.get(`${API_BASE}/places/details`, { params: { place_id: placeId } });
      const data = resp.data;
      onPlaceSelect({ place_id: placeId, name: displayText, formatted_address: data.formattedAddress || displayText, types: data.types || [], location: data.location || null });
      onChange(data.formattedAddress || displayText);
    } catch {
      onPlaceSelect({ place_id: placeId, name: displayText, formatted_address: displayText, types: [], location: null });
    }
  };

  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <input type="text" value={value} onChange={handleInput} onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
        required={required} className="input-luxury w-full bg-transparent" placeholder={placeholder} data-testid={testId} autoComplete="off" />
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#1A1A1A]/15 shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((s) => {
            const pred = s.placePrediction;
            const mainText = pred?.structuredFormat?.mainText?.text || pred?.text?.text || "";
            const secondaryText = pred?.structuredFormat?.secondaryText?.text || "";
            return (
              <button key={pred?.placeId || mainText} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => handleSelect(s)}
                className="w-full text-left px-4 py-3 hover:bg-[#EDEBE4] transition-colors border-b border-[#1A1A1A]/5 last:border-b-0">
                <p className="text-[#1A1A1A] text-sm font-medium">{mainText}</p>
                {secondaryText && <p className="text-[#1A1A1A]/50 text-xs">{secondaryText}</p>}
              </button>
            );
          })}
          {loading && <div className="px-4 py-3 text-[#1A1A1A]/40 text-xs">Recherche...</div>}
        </div>
      )}
    </div>
  );
};

// ─── Detection logic ───
const detectPlace = (place) => {
  if (!place) return null;
  const types = place.types || [];
  const combined = ((place.name || "") + " " + (place.formatted_address || "")).toLowerCase();
  if (types.includes("airport") || /cdg|charles de gaulle|orly|le bourget|aéroport|aeroport/.test(combined)) return "airport";
  if (types.includes("train_station") || types.includes("transit_station") || /gare du nord|gare de lyon|gare de l'est|gare montparnasse|gare saint-lazare|gare d'austerlitz|gare de bercy/.test(combined)) return "station";
  if (/disney/.test(combined)) return "disneyland";
  if (/château de versailles|chateau de versailles/.test(combined) || (/versailles/.test(combined) && /château|chateau|palace/.test(combined))) return "versailles";
  return null;
};

const isParis = (place) => {
  if (!place || detectPlace(place)) return false;
  const addr = (place.formatted_address || "").toLowerCase();
  return /\bparis\b/.test(addr) || /\b75\d{3}\b/.test(addr);
};

const resolvePackage = (pickupPlace, dropoffPlace) => {
  const pickupType = detectPlace(pickupPlace);
  const dropoffType = detectPlace(dropoffPlace);
  const pickupIsParis = isParis(pickupPlace);
  const dropoffIsParis = isParis(dropoffPlace);
  const specialInfo = { hasAirport: pickupType === "airport" || dropoffType === "airport", hasStation: pickupType === "station" || dropoffType === "station", withinParis: pickupIsParis && dropoffIsParis };
  if (pickupType && dropoffIsParis) {
    if (pickupType === "airport") return { id: "airport_arrivee", type: "airport_arrival", ...specialInfo };
    if (pickupType === "station") return { id: "ville_gares_arrivee", type: "station_arrival", ...specialInfo };
    if (pickupType === "disneyland") return { id: "disneyland", type: "disneyland", ...specialInfo };
    if (pickupType === "versailles") return { id: "versailles", type: "versailles", ...specialInfo };
  }
  if (dropoffType && pickupIsParis) {
    if (dropoffType === "airport") return { id: "airport", type: "airport", ...specialInfo };
    if (dropoffType === "station") return { id: "ville_gares", type: "station", ...specialInfo };
    if (dropoffType === "disneyland") return { id: "disneyland", type: "disneyland", ...specialInfo };
    if (dropoffType === "versailles") return { id: "versailles", type: "versailles", ...specialInfo };
  }
  return { id: "custom_km", type: "km", ...specialInfo };
};

const DETECTION_LABELS = {
  airport: "Forfait Aéroport", airport_arrival: "Forfait Aéroport (arrivée)",
  station: "Forfait Gares", station_arrival: "Forfait Gares (arrivée)",
  disneyland: "Forfait Disneyland", versailles: "Forfait Versailles", km: "Trajet sur mesure (au km)",
};
const DETECTION_ICONS = { airport: AirplaneTakeoff, airport_arrival: AirplaneTakeoff, station: Train, station_arrival: Train, disneyland: CastleTurret, versailles: CastleTurret, km: NavigationArrow };

// ─── Status Badges ───
const STATUS_LABELS = { draft: "Brouillon", pending_payment: "Attente paiement", paid: "Payé", confirmed: "Confirmée", driver_assigned: "Chauffeur assigné", en_route: "En route", in_progress: "En cours", completed: "Effectuée", cancelled: "Annulée" };
const STATUS_COLORS = { draft: "bg-gray-200 text-gray-700", pending_payment: "bg-yellow-100 text-yellow-800", paid: "bg-blue-100 text-blue-800", confirmed: "bg-green-100 text-green-800", driver_assigned: "bg-indigo-100 text-indigo-800", completed: "bg-green-200 text-green-900", cancelled: "bg-red-100 text-red-800" };
const Badge = ({ status }) => <span className={`px-2 py-0.5 text-xs font-medium rounded-sm ${STATUS_COLORS[status] || "bg-gray-100 text-gray-800"}`}>{STATUS_LABELS[status] || status}</span>;
const VEHICLE_NAMES = { classe_e: "Mercedes Classe E", classe_v: "Mercedes Classe V", classe_s: "Mercedes Classe S" };
const REFUND_LABELS = { refunded: "Remboursée", not_refunded: "Non remboursée", partial: "Remboursement partiel" };
const PAYMENT_METHOD_LABELS = { online: "Paiement en ligne", onboard: "Règlement à bord" };

const bookableVehicles = [
  { id: "classe_e", name: "Mercedes Classe E", capacity: 3, image: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/stw5s5ky_703D55C7-6491-4A1D-BBD1-3724BB576F38.png" },
  { id: "classe_s", name: "Mercedes Classe S", capacity: 3, image: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/8up45an8_8CF1CAA1-D304-4DC3-99AE-C4B9AE562F84.jpeg" },
  { id: "classe_v", name: "Mercedes Classe V", capacity: 7, image: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/bat48e3f_E8F9F042-84D5-4B63-87E0-68156E0CD47E.png" },
];

// ─── Payout Preferences Component ───
const PayoutPreferences = () => {
  const [method, setMethod] = useState("none");
  const [iban, setIban] = useState("");
  const [holder, setHolder] = useState("");
  const [bic, setBic] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    ax.get("/partner/payout").then(({ data }) => {
      setMethod(data.payout_method || "none");
      setIban(data.iban || "");
      setHolder(data.iban_holder || "");
      setBic(data.iban_bic || "");
      setLoaded(true);
    }).catch((err) => { console.error("Payout load error:", err); setLoaded(true); });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await ax.post("/partner/payout", { payout_method: method, iban, iban_holder: holder, iban_bic: bic });
      toast.success("Préférences de paiement enregistrées");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  if (!loaded) return null;

  return (
    <div className="bg-white border border-[#1A1A1A]/10 p-6" data-testid="payout-preferences">
      <h3 className="font-heading text-lg text-[#1A1A1A] mb-4">Mode de versement des commissions</h3>
      <p className="text-[#1A1A1A]/60 text-sm mb-5">Choisissez comment vous souhaitez recevoir vos commissions.</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <button type="button" onClick={() => setMethod("iban")}
          className={`p-4 border text-left transition-all ${method === "iban" ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A] hover:border-[#1A1A1A]/40"}`}
          data-testid="payout-iban-btn">
          <p className="text-sm font-medium mb-1">Virement bancaire</p>
          <p className={`text-xs ${method === "iban" ? "text-white/70" : "text-[#1A1A1A]/50"}`}>Versement par virement SEPA chaque mardi</p>
        </button>
        <button type="button" onClick={() => setMethod("cash")}
          className={`p-4 border text-left transition-all ${method === "cash" ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A] hover:border-[#1A1A1A]/40"}`}
          data-testid="payout-cash-btn">
          <p className="text-sm font-medium mb-1">Remise en main propre</p>
          <p className={`text-xs ${method === "cash" ? "text-white/70" : "text-[#1A1A1A]/50"}`}>Commission remise en espèces ou autre arrangement</p>
        </button>
      </div>
      {method === "iban" && (
        <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 mb-6">
          <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Titulaire du compte *</label>
            <input type="text" value={holder} onChange={(e) => setHolder(e.target.value)} className="input-luxury w-full bg-transparent" placeholder="Nom et prénom ou raison sociale" data-testid="payout-holder" /></div>
          <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">IBAN *</label>
            <input type="text" value={iban} onChange={(e) => setIban(e.target.value)} className="input-luxury w-full bg-transparent font-mono" placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" data-testid="payout-iban" /></div>
          <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">BIC / SWIFT</label>
            <input type="text" value={bic} onChange={(e) => setBic(e.target.value)} className="input-luxury w-full bg-transparent font-mono" placeholder="BNPAFRPP" data-testid="payout-bic" /></div>
        </motion.div>
      )}
      {method !== "none" && (
        <button onClick={handleSave} disabled={saving || (method === "iban" && (!iban || !holder))}
          className="btn-dark text-xs px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="payout-save-btn">
          {saving ? "Enregistrement..." : "Enregistrer mes préférences"}
        </button>
      )}
    </div>
  );
};

// ─── Team Tab (Operators) ───
const TeamTab = ({ operators, onReload }) => {
  const [adding, setAdding] = useState(false);
  const [newOp, setNewOp] = useState({ name: "", role: "", phone: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editOp, setEditOp] = useState({});

  const handleAdd = async () => {
    if (!newOp.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      await ax.post("/partner/operators", newOp);
      toast.success("Membre ajouté");
      setNewOp({ name: "", role: "", phone: "", email: "" });
      setAdding(false);
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    setSaving(true);
    try {
      await ax.put(`/partner/operators/${id}`, editOp);
      toast.success("Membre modifié");
      setEditingId(null);
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce membre ?")) return;
    try {
      await ax.delete(`/partner/operators/${id}`);
      toast.success("Membre supprimé");
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="team-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl text-[#1A1A1A]">Mon équipe</h2>
        <button onClick={() => setAdding(!adding)} className="text-xs px-4 py-2 bg-[#1A1A1A] text-white hover:bg-[#333] transition-colors flex items-center gap-2" data-testid="add-operator-btn">
          <Plus size={14} /> Ajouter un membre
        </button>
      </div>
      <p className="text-[#1A1A1A]/60 text-sm mb-6">Répertoriez vos réceptionnistes et concierges. Ils apparaîtront dans le sélecteur lors des commandes.</p>

      {adding && (
        <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#1A1A1A]/10 p-5 mb-4" data-testid="add-operator-form">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-1">Nom *</label>
              <input type="text" value={newOp.name} onChange={(e) => setNewOp(p => ({ ...p, name: e.target.value }))} className="input-luxury w-full bg-transparent text-sm" placeholder="Sophie Martin" data-testid="new-op-name" /></div>
            <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-1">Poste</label>
              <input type="text" value={newOp.role} onChange={(e) => setNewOp(p => ({ ...p, role: e.target.value }))} className="input-luxury w-full bg-transparent text-sm" placeholder="Réceptionniste, Concierge..." data-testid="new-op-role" /></div>
            <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-1">Téléphone</label>
              <input type="tel" value={newOp.phone} onChange={(e) => setNewOp(p => ({ ...p, phone: e.target.value }))} className="input-luxury w-full bg-transparent text-sm" placeholder="+33..." data-testid="new-op-phone" /></div>
            <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-1">Email</label>
              <input type="email" value={newOp.email} onChange={(e) => setNewOp(p => ({ ...p, email: e.target.value }))} className="input-luxury w-full bg-transparent text-sm" placeholder="email" data-testid="new-op-email" /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={saving} className="text-xs px-5 py-2 bg-[#1A1A1A] text-white hover:bg-[#333] disabled:opacity-50" data-testid="save-new-op">
              {saving ? "Ajout..." : "Ajouter"}
            </button>
            <button onClick={() => setAdding(false)} className="text-xs px-5 py-2 border border-[#1A1A1A]/20">Annuler</button>
          </div>
        </motion.div>
      )}

      <div className="space-y-2">
        {operators.length === 0 && !adding && <p className="text-[#1A1A1A]/50 text-sm py-8 text-center">Aucun membre enregistré</p>}
        {operators.map(o => (
          <div key={o.id} className="bg-white border border-[#1A1A1A]/10 p-4 flex items-center justify-between" data-testid={`operator-${o.id}`}>
            {editingId === o.id ? (
              <div className="flex-1 grid sm:grid-cols-4 gap-3 mr-3">
                <input type="text" value={editOp.name ?? o.name} onChange={(e) => setEditOp(p => ({ ...p, name: e.target.value }))} className="input-luxury bg-transparent text-sm" />
                <input type="text" value={editOp.role ?? o.role} onChange={(e) => setEditOp(p => ({ ...p, role: e.target.value }))} className="input-luxury bg-transparent text-sm" placeholder="Poste" />
                <input type="tel" value={editOp.phone ?? o.phone} onChange={(e) => setEditOp(p => ({ ...p, phone: e.target.value }))} className="input-luxury bg-transparent text-sm" placeholder="Tél" />
                <div className="flex gap-2">
                  <button onClick={() => handleUpdate(o.id)} className="text-xs px-3 py-1.5 bg-[#1A1A1A] text-white">OK</button>
                  <button onClick={() => setEditingId(null)} className="text-xs px-3 py-1.5 border border-[#1A1A1A]/20">X</button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <p className="text-[#1A1A1A] text-sm font-medium">{o.name}</p>
                  <p className="text-[#1A1A1A]/50 text-xs">{o.role}{o.phone ? ` · ${o.phone}` : ""}{o.email ? ` · ${o.email}` : ""}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setEditingId(o.id); setEditOp({ name: o.name, role: o.role, phone: o.phone }); }}
                    className="text-xs px-3 py-1.5 border border-[#1A1A1A]/20 hover:bg-[#EDEBE4] transition-colors" data-testid={`edit-op-${o.id}`}>Modifier</button>
                  <button onClick={() => handleDelete(o.id)}
                    className="text-xs px-3 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-op-${o.id}`}>Supprimer</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


// ─── Profile Tab ───
const ProfileTab = () => {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    ax.get("/partner/profile").then(({ data }) => { setProfile(data); setForm(data); }).catch((err) => { console.error("Profile load error:", err); });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await ax.put("/partner/profile", form);
      toast.success("Profil mis à jour");
      setProfile(form);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (!profile) return null;

  return (
    <div data-testid="profile-tab">
      <h2 className="font-heading text-xl text-[#1A1A1A] mb-6">Mon profil</h2>
      <div className="bg-white border border-[#1A1A1A]/10 p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          {[
            { key: "full_name", label: "Nom complet" },
            { key: "company_name", label: "Société" },
            { key: "email", label: "Email", type: "email" },
            { key: "phone", label: "Téléphone", type: "tel" },
            { key: "position", label: "Poste" },
          ].map(f => (
            <div key={f.key}>
              <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">{f.label}</label>
              <input type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                className="input-luxury w-full bg-transparent" data-testid={`profile-${f.key}`} />
            </div>
          ))}
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-dark text-xs px-6 py-3 disabled:opacity-50" data-testid="profile-save-btn">
          {saving ? "Enregistrement..." : "Enregistrer les modifications"}
        </button>
      </div>
    </div>
  );
};


// ─── Booking Card (used in history) ───
const BookingCard = ({ b, onEdit, onConfirm, onDeleteDraft }) => (
  <div className={`bg-white border p-5 ${b.status === "draft" ? "border-[#1A1A1A]/30 border-dashed" : "border-[#1A1A1A]/10"}`} data-testid={`booking-card-${b.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <div>
        <p className="text-[#1A1A1A] font-medium text-sm">{b.client_name || <span className="text-[#1A1A1A]/30 italic">Client non renseigné</span>}</p>
        <p className="text-[#1A1A1A]/50 text-xs">
          {b.pickup_date ? `${b.pickup_date} à ${b.pickup_time || "—"}` : "Date non définie"}
          {" · "}{VEHICLE_NAMES[b.vehicle_type] || b.vehicle_type}
        </p>
      </div>
      <div className="text-right">
        <Badge status={b.status} />
        {b.total_amount > 0 && <p className="font-heading text-lg text-[#1A1A1A] mt-1">{b.total_amount}€</p>}
      </div>
    </div>
    <div className="text-xs text-[#1A1A1A]/60 space-y-1">
      {b.pickup_address && <p>Pick-up : {b.pickup_address}</p>}
      {b.dropoff_address && <p>Drop off : {b.dropoff_address}</p>}
      {b.flight_train_number && <p>Vol/Train : {b.flight_train_number}</p>}
      {(b.client_phone || b.client_email) && (
        <p>Contact : {b.client_phone}{b.client_email ? ` · ${b.client_email}` : ""}</p>
      )}
      <p>{b.passengers} pax · {b.luggage} bagages</p>
      {b.payment_method && <p className="text-[#1A1A1A]/40">
        {b.payment_method === "onboard" ? <><Money size={12} className="inline mr-1" />Règlement à bord</> : <><CreditCard size={12} className="inline mr-1" />Paiement en ligne</>}
      </p>}
      {b.ordered_by && <p className="text-[#1A1A1A]/40">Commandé par : {b.ordered_by}</p>}
      {b.notes && <p>Notes : {b.notes}</p>}
      {b.extra_stops && b.extra_stops.length > 0 && (
        <div className="mt-1 pl-3 border-l-2 border-[#1A1A1A]/10">
          <p className="text-[#1A1A1A]/50 text-[10px] uppercase tracking-wider mb-0.5">Arrêt{b.extra_stops.length > 1 ? "s" : ""} supplémentaire{b.extra_stops.length > 1 ? "s" : ""}</p>
          {b.extra_stops.map((s, i) => (
            <p key={s.name || s.address || i} className="text-[#1A1A1A]/60">{s.name ? `${s.name} — ` : ""}{s.address}{s.notes ? ` (${s.notes})` : ""}</p>
          ))}
          {b.extra_stops_total > 0 && <p className="text-[#1A1A1A]/40">+{b.extra_stops_total}€</p>}
        </div>
      )}
      {b.internal_notes && <p className="text-amber-700"><ChatText size={12} className="inline mr-1" />Commentaires : {b.internal_notes}</p>}
      {b.driver && <p className="text-indigo-600">Chauffeur : {b.driver.name} · {b.driver.vehicle_model} · {b.driver.plate}{b.driver.phone ? ` · ${b.driver.phone}` : ""}</p>}
      {b.status !== "draft" && b.commission_amount > 0 && <p className="text-green-700 mt-1">Commission : {b.commission_amount}€</p>}
      {b.status === "cancelled" && b.refund_status && (
        <p className="text-red-600 mt-1"><Warning size={12} className="inline mr-1" />{REFUND_LABELS[b.refund_status] || b.refund_status}{b.refund_amount > 0 ? ` (${b.refund_amount}€)` : ""}</p>
      )}
    </div>
    {/* Actions */}
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {b.status === "draft" && (
        <>
          <button onClick={() => onEdit(b)} className="text-xs px-3 py-1.5 bg-[#1A1A1A] text-white hover:bg-[#333] transition-colors flex items-center gap-1.5" data-testid={`edit-draft-${b.id}`}>
            <PencilSimple size={12} /> Modifier
          </button>
          <button onClick={() => onConfirm(b.id)} className="text-xs px-3 py-1.5 bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-1.5" data-testid={`confirm-draft-${b.id}`}>
            <Check size={12} weight="bold" /> Valider la commande
          </button>
          {onDeleteDraft && (
            <button onClick={() => onDeleteDraft(b.id)} className="text-xs px-3 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 transition-colors flex items-center gap-1.5" data-testid={`delete-draft-${b.id}`}>
              Supprimer
            </button>
          )}
        </>
      )}
      {b.payment_link && b.status === "pending_payment" && (
        <div className="flex items-center gap-2 w-full">
          <input type="text" readOnly value={b.payment_link} className="input-luxury bg-[#EDEBE4] text-xs flex-1" />
          <button onClick={() => { navigator.clipboard.writeText(b.payment_link); toast.success("Lien copié !"); }}
            className="text-xs px-3 py-2 bg-[#1A1A1A] text-white hover:bg-[#333] transition-colors flex items-center gap-1.5 whitespace-nowrap">
            <Copy size={12} /> Copier
          </button>
        </div>
      )}
      <button onClick={() => { window.open(`${API}/api/connect/bookings/${b.id}/pdf`, "_blank"); }}
        className="text-xs px-3 py-1.5 border border-[#1A1A1A]/20 hover:bg-[#EDEBE4] transition-colors flex items-center gap-1.5" data-testid={`download-pdf-${b.id}`}>
        <DownloadSimple size={12} /> Bon de commande
      </button>
    </div>
  </div>
);

// ─── History Tab with sub-filters ───
const HistoryTab = ({ dashboard, onEdit, onConfirm, onDeleteDraft }) => {
  const [subTab, setSubTab] = useState("upcoming");
  const upcomingCount = (dashboard.drafts?.length || 0) + (dashboard.upcoming?.length || 0);
  const pastCount = dashboard.past?.length || 0;

  return (
    <div data-testid="history-tab">
      {/* Sub-tabs */}
      <div className="flex items-center gap-0 mb-6 border-b border-[#1A1A1A]/10">
        <button onClick={() => setSubTab("upcoming")}
          className={`py-2.5 px-5 text-sm border-b-2 transition-colors ${subTab === "upcoming" ? "border-[#1A1A1A] text-[#1A1A1A] font-medium" : "border-transparent text-[#1A1A1A]/40 hover:text-[#1A1A1A]/70"}`}
          data-testid="history-subtab-upcoming">
          À venir {upcomingCount > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-[#1A1A1A]/10 rounded-sm">{upcomingCount}</span>}
        </button>
        <button onClick={() => setSubTab("past")}
          className={`py-2.5 px-5 text-sm border-b-2 transition-colors ${subTab === "past" ? "border-[#1A1A1A] text-[#1A1A1A] font-medium" : "border-transparent text-[#1A1A1A]/40 hover:text-[#1A1A1A]/70"}`}
          data-testid="history-subtab-past">
          Passées {pastCount > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-[#1A1A1A]/10 rounded-sm">{pastCount}</span>}
        </button>
      </div>

      {subTab === "upcoming" && (
        <div className="space-y-6">
          {/* Drafts */}
          {dashboard.drafts && dashboard.drafts.length > 0 && (
            <div>
              <h3 className="text-[#1A1A1A]/60 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                <FloppyDisk size={14} /> Brouillons ({dashboard.drafts.length})
              </h3>
              <div className="space-y-3">
                {dashboard.drafts.map(b => <BookingCard key={b.id} b={b} onEdit={onEdit} onConfirm={onConfirm} onDeleteDraft={onDeleteDraft} />)}
              </div>
            </div>
          )}
          {/* Upcoming confirmed */}
          <div>
            <h3 className="text-[#1A1A1A]/60 text-xs uppercase tracking-wider mb-3">Confirmées à venir ({dashboard.upcoming?.length || 0})</h3>
            <div className="space-y-3">
              {(!dashboard.upcoming || dashboard.upcoming.length === 0) && <p className="text-[#1A1A1A]/40 text-sm py-6 text-center">Aucune commande à venir</p>}
              {dashboard.upcoming?.map(b => <BookingCard key={b.id} b={b} onEdit={onEdit} onConfirm={onConfirm} />)}
            </div>
          </div>
        </div>
      )}

      {subTab === "past" && (
        <div>
          <div className="space-y-3">
            {pastCount === 0 && <p className="text-[#1A1A1A]/40 text-sm py-6 text-center">Aucune commande passée</p>}
            {dashboard.past?.map(b => <BookingCard key={b.id} b={b} onEdit={onEdit} onConfirm={onConfirm} />)}
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Main Component ───
const PartnerDashboard = () => {
  const { user, logout } = useConnectAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("booking");
  const [vehiclePrices, setVehiclePrices] = useState({});
  const [kmRates, setKmRates] = useState({});
  const [dashboard, setDashboard] = useState(null);

  // Booking form state
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState();
  const [missionType, setMissionType] = useState("simple");
  const [hourlyDuration, setHourlyDuration] = useState("hourly_3h");
  const [detectedPkg, setDetectedPkg] = useState(null);
  const [distanceKm, setDistanceKm] = useState(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [pickupPlace, setPickupPlace] = useState(null);
  const [dropoffPlace, setDropoffPlace] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [customPrice, setCustomPrice] = useState("");
  const [commissionPreview, setCommissionPreview] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("online");
  const [editingDraft, setEditingDraft] = useState(null);
  const [extraStops, setExtraStops] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState("");
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    client_name: "", client_email: "", client_phone: "",
    vehicle_type: "classe_e", pickup_address: "", dropoff_address: "",
    flight_train_number: "", pickup_time: "09:00", passengers: 1, luggage: 0, notes: "", internal_notes: "",
  });

  useEffect(() => {
    axios.get(`${API_BASE}/packages`).then(r => {
      setVehiclePrices(r.data.vehicle_prices || {});
      setKmRates(r.data.km_rates || {});
    }).catch((err) => { console.error("Packages load error:", err); });
  }, []);

  const loadDashboard = useCallback(async () => {
    try {
      const [dashRes, opsRes] = await Promise.all([
        ax.get("/partner/dashboard"),
        ax.get("/partner/operators"),
      ]);
      setDashboard(dashRes.data);
      setOperators(opsRes.data.operators || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/connect");
    }
  }, [navigate]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const updateDetection = useCallback(async (pickup, dropoff) => {
    if (!pickup || !dropoff) { setDetectedPkg(null); setDistanceKm(null); return; }
    const pkg = resolvePackage(pickup, dropoff);
    setDetectedPkg(pkg);
    if (pkg.id === "custom_km" && pickup.location && dropoff.location) {
      setCalculatingDistance(true);
      try {
        const resp = await axios.get(`${API_BASE}/places/distance`, {
          params: { origin_lat: pickup.location.latitude, origin_lng: pickup.location.longitude, dest_lat: dropoff.location.latitude, dest_lng: dropoff.location.longitude, origin_place_id: pickup.place_id, dest_place_id: dropoff.place_id }
        });
        setDistanceKm(resp.data.distance_km);
      } catch (err) { console.error("Distance calc error:", err); setDistanceKm(null); }
      finally { setCalculatingDistance(false); }
    } else { setDistanceKm(null); }
  }, []);

  const enrichAddressWithPlaceName = (place) => {
    if (!place) return place.formatted_address || "";
    const name = place.name || "";
    const addr = place.formatted_address || "";
    if (!name || addr.toLowerCase().includes(name.toLowerCase())) return addr;
    // Always prefix the place name (hotel, restaurant, airport, station, etc.)
    return `${name} — ${addr}`;
  };

  const onPickupPlaceSelect = (place) => { setPickupPlace(place); const enriched = enrichAddressWithPlaceName(place); setFormData(p => ({ ...p, pickup_address: enriched })); if (missionType === "simple") updateDetection(place, dropoffPlace); };
  const onDropoffPlaceSelect = (place) => { setDropoffPlace(place); const enriched = enrichAddressWithPlaceName(place); setFormData(p => ({ ...p, dropoff_address: enriched })); if (missionType === "simple") updateDetection(pickupPlace, place); };

  const calculateTotal = () => {
    if (missionType === "hourly") return vehiclePrices[formData.vehicle_type]?.[hourlyDuration] || 0;
    if (!detectedPkg) return 0;
    if (detectedPkg.id === "custom_km") {
      const km = kmRates[formData.vehicle_type];
      if (!km) return 0;
      const minimum = detectedPkg.withinParis ? km.minimum_paris : km.minimum_outside;
      if (!distanceKm) return minimum;
      return Math.max(distanceKm * km.rate, minimum);
    }
    return vehiclePrices[formData.vehicle_type]?.[detectedPkg.id] || 0;
  };

  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const fetchPreview = useCallback(async (baseTotal, customPriceVal, vehicleType, pickupAddr, dropoffAddr, pkgId) => {
    if (baseTotal <= 0) { setCommissionPreview(null); return; }
    try {
      const cp = customPriceVal && parseFloat(customPriceVal) >= baseTotal ? parseFloat(customPriceVal) : null;
      const { data } = await ax.post("/partner/price-preview", {
        vehicle_type: vehicleType, pickup_address: pickupAddr || "", dropoff_address: dropoffAddr || "",
        custom_price: cp, package_id: pkgId || null,
      });
      setCommissionPreview(data);
    } catch (err) { console.error("Commission preview error:", err); setCommissionPreview(null); }
  }, []);

  useEffect(() => {
    const baseTotal = calculateTotal();
    const pkgId = missionType === "hourly" ? hourlyDuration : detectedPkg?.id;
    fetchPreview(baseTotal, customPrice, formData.vehicle_type, formData.pickup_address, formData.dropoff_address, pkgId);
  }, [customPrice, formData.vehicle_type, formData.pickup_address, formData.dropoff_address, detectedPkg, distanceKm, missionType, hourlyDuration, vehiclePrices, fetchPreview]);

  const buildPayload = (isDraft) => {
    const packageId = missionType === "hourly" ? hourlyDuration : (detectedPkg?.id || "custom_km");
    return {
      client_name: formData.client_name, client_email: formData.client_email || "", client_phone: formData.client_phone,
      pickup_address: formData.pickup_address, dropoff_address: formData.dropoff_address || "",
      pickup_date: date ? format(date, "yyyy-MM-dd") : "", pickup_time: formData.pickup_time,
      flight_train_number: formData.flight_train_number, passengers: formData.passengers, luggage: formData.luggage,
      vehicle_type: formData.vehicle_type, notes: formData.notes, internal_notes: formData.internal_notes,
      custom_price: customPrice && parseFloat(customPrice) > total ? parseFloat(customPrice) : null,
      payment_method: paymentMethod,
      is_draft: isDraft,
      extra_stops: extraStops,
      ordered_by: selectedOperator || "",
    };
  };

  const handleSubmit = async (e, isDraft = false) => {
    e.preventDefault();
    setLoading(true);
    setBookingResult(null);
    try {
      if (editingDraft) {
        // Update existing draft
        const payload = buildPayload(true);
        delete payload.is_draft;
        await ax.put(`/partner/bookings/${editingDraft}`, payload);
        if (!isDraft) {
          const { data } = await ax.post(`/partner/bookings/${editingDraft}/confirm`);
          setBookingResult({ success: true, booking_id: editingDraft, payment_url: data.payment_url, payment_method: data.payment_method, total_amount: total, ...commissionPreview });
        } else {
          toast.success("Brouillon mis à jour");
          setEditingDraft(null);
          resetForm();
        }
        loadDashboard();
      } else {
        const payload = buildPayload(isDraft);
        const { data } = await ax.post("/partner/bookings", payload);
        if (isDraft) {
          toast.success("Brouillon enregistré");
          resetForm();
          loadDashboard();
          setTab("history");
        } else {
          setBookingResult(data);
          if (data.payment_url) {
            try { await navigator.clipboard.writeText(data.payment_url); } catch { /* clipboard not available */ }
            toast.success("Réservation créée — lien de paiement copié !");
          } else if (data.payment_method === "onboard") {
            toast.success("Réservation confirmée — règlement à bord");
          } else {
            toast.success("Réservation créée !");
          }
          loadDashboard();
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally { setLoading(false); }
  };

  const resetForm = () => {
    setBookingResult(null); setCommissionPreview(null); setCustomPrice(""); setPaymentMethod("online"); setEditingDraft(null); setExtraStops([]); setSelectedOperator("");
    setDetectedPkg(null); setDistanceKm(null); setPickupPlace(null); setDropoffPlace(null); setDate(undefined);
    setFormData({ client_name: "", client_email: "", client_phone: "", vehicle_type: "classe_e", pickup_address: "", dropoff_address: "", flight_train_number: "", pickup_time: "09:00", passengers: 1, luggage: 0, notes: "", internal_notes: "" });
  };

  const loadDraftIntoForm = (b) => {
    setEditingDraft(b.id);
    setFormData({
      client_name: b.client_name || "", client_email: b.client_email || "", client_phone: b.client_phone || "",
      vehicle_type: b.vehicle_type || "classe_e", pickup_address: b.pickup_address || "", dropoff_address: b.dropoff_address || "",
      flight_train_number: b.flight_train_number || "", pickup_time: b.pickup_time || "09:00",
      passengers: b.passengers || 1, luggage: b.luggage || 0, notes: b.notes || "", internal_notes: b.internal_notes || "",
    });
    setPaymentMethod(b.payment_method || "online");
    setExtraStops(b.extra_stops || []);
    setSelectedOperator(b.ordered_by || "");
    if (b.pickup_date) {
      try { setDate(new Date(b.pickup_date + "T00:00:00")); } catch { setDate(undefined); }
    }
    setTab("booking");
    window.scrollTo(0, 0);
  };

  const confirmDraft = async (bookingId) => {
    try {
      const { data } = await ax.post(`/partner/bookings/${bookingId}/confirm`);
      if (data.payment_url) {
        try { await navigator.clipboard.writeText(data.payment_url); } catch { /* clipboard not available */ }
        toast.success("Commande confirmée — lien de paiement copié !");
      } else {
        toast.success("Commande confirmée !");
      }
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteDraft = async (bookingId) => {
    if (!window.confirm("Supprimer ce brouillon ?")) return;
    try {
      await ax.delete(`/partner/bookings/${bookingId}`);
      toast.success("Brouillon supprimé");
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleLogout = async () => { await logout(); navigate("/connect"); };
  const total = calculateTotal();
  const isAirportOrStation = detectedPkg && (detectedPkg.hasAirport || detectedPkg.hasStation);
  const DetectedIcon = detectedPkg ? DETECTION_ICONS[detectedPkg.type] : null;

  const generateTimeSlots = () => {
    const slots = [];
    for (let h = 0; h < 24; h++) for (let m = 0; m < 60; m += 15) slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    return slots;
  };
  const timeSlots = generateTimeSlots();
  const draftCount = dashboard?.drafts?.length || 0;

  return (
    <main className="min-h-screen bg-[#EDEBE4]" data-testid="partner-dashboard">
      {/* Top bar */}
      <div className="bg-[#1A1A1A] text-white">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-heading text-sm tracking-[0.25em] uppercase">Altura Connect</span>
            <span className="text-white/40 text-xs">{user?.company_name}</span>
          </div>
          <button onClick={handleLogout} className="text-white/60 hover:text-white text-xs flex items-center gap-2" data-testid="partner-logout">
            <SignOut size={14} /> Déconnexion
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-[#1A1A1A]/10">
        <div className="max-w-6xl mx-auto px-6 flex gap-0">
          {[
            { id: "booking", label: editingDraft ? "Modifier le brouillon" : "Nouvelle réservation" },
            { id: "history", label: `Historique${draftCount > 0 ? ` (${draftCount})` : ""}` },
            { id: "commissions", label: "Commissions" },
            { id: "team", label: "Équipe" },
            { id: "profile", label: "Mon profil" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`py-3 px-5 text-sm border-b-2 transition-colors ${tab === t.id ? "border-[#1A1A1A] text-[#1A1A1A] font-medium" : "border-transparent text-[#1A1A1A]/50 hover:text-[#1A1A1A]/80"}`}
              data-testid={`partner-tab-${t.id}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* ═══ BOOKING TAB ═══ */}
        {tab === "booking" && (
          <>
            {bookingResult && !bookingResult.error && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-green-50 border border-green-200 p-5 mb-6">
                <div className="flex items-start gap-3">
                  <Check size={20} weight="bold" className="text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-green-800 text-sm font-medium">
                      {bookingResult.payment_method === "onboard" ? "Commande confirmée — Règlement à bord" : "Réservation créée avec succès"}
                    </p>
                    <p className="text-green-700 text-xs mt-1">
                      {bookingResult.total_amount > 0 && <>Client paie : {bookingResult.total_amount}€</>}
                      {bookingResult.commission_amount > 0 && <>{" · "}Votre commission : {bookingResult.commission_amount}€</>}
                    </p>
                    {bookingResult.payment_url && (
                      <div className="mt-3 flex items-center gap-2">
                        <input type="text" readOnly value={bookingResult.payment_url} className="input-luxury bg-white text-xs flex-1" data-testid="payment-link-input" />
                        <button onClick={() => { navigator.clipboard.writeText(bookingResult.payment_url); toast.success("Lien copié !"); }}
                          className="text-xs px-3 py-2 bg-[#1A1A1A] text-white hover:bg-[#333] transition-colors flex items-center gap-1.5 whitespace-nowrap">
                          <Copy size={12} /> Copier le lien
                        </button>
                      </div>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <button onClick={() => { window.open(`${API}/api/connect/bookings/${bookingResult.booking_id}/pdf`, "_blank"); }}
                        className="text-xs px-3 py-2 border border-[#1A1A1A]/20 hover:bg-[#EDEBE4] transition-colors flex items-center gap-1.5" data-testid="download-booking-pdf">
                        <DownloadSimple size={12} /> Télécharger le bon de commande
                      </button>
                    </div>
                    <button onClick={resetForm} className="text-green-700 text-xs mt-3 underline">Nouvelle réservation</button>
                  </div>
                </div>
              </motion.div>
            )}

            {!bookingResult && (
              <div className="bg-white border border-[#1A1A1A]/10 p-8 lg:p-10">
                {editingDraft && (
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-amber-700 text-xs flex items-center gap-2"><PencilSimple size={14} /> Modification du brouillon</p>
                    <button onClick={resetForm} className="text-[#1A1A1A]/50 text-xs hover:text-[#1A1A1A]">Annuler la modification</button>
                  </div>
                )}
                <h2 className="font-heading text-2xl text-[#1A1A1A] mb-8">Réserver un transport</h2>
                <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6" data-testid="partner-booking-form">
                  {/* Mission Type */}
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-3">Type de mission *</label>
                    <div className="grid grid-cols-2 gap-0">
                      <button type="button" onClick={() => { setMissionType("simple"); setDetectedPkg(null); setDistanceKm(null); }}
                        className={`p-4 border text-left transition-all ${missionType === "simple" ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A]"}`} data-testid="mission-simple-btn">
                        <div className="flex items-center gap-3 mb-1"><MapTrifold size={20} weight={missionType === "simple" ? "bold" : "light"} /><span className="text-sm font-medium">Trajet simple</span></div>
                        <p className={`text-xs ${missionType === "simple" ? "text-white/70" : "text-[#1A1A1A]/50"}`}>Point A → Point B</p>
                      </button>
                      <button type="button" onClick={() => { setMissionType("hourly"); setDetectedPkg(null); setDistanceKm(null); }}
                        className={`p-4 border border-l-0 text-left transition-all ${missionType === "hourly" ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A]"}`} data-testid="mission-hourly-btn">
                        <div className="flex items-center gap-3 mb-1"><Timer size={20} weight={missionType === "hourly" ? "bold" : "light"} /><span className="text-sm font-medium">Mise à disposition</span></div>
                        <p className={`text-xs ${missionType === "hourly" ? "text-white/70" : "text-[#1A1A1A]/50"}`}>3h, 6h ou 10h</p>
                      </button>
                    </div>
                  </div>

                  {/* Client info */}
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Nom du client</label>
                      <input type="text" name="client_name" value={formData.client_name} onChange={handleChange} className="input-luxury w-full bg-transparent" placeholder="Nom complet" data-testid="input-client-name" /></div>
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Téléphone</label>
                      <input type="tel" name="client_phone" value={formData.client_phone} onChange={handleChange} className="input-luxury w-full bg-transparent" placeholder="+33..." data-testid="input-client-phone" /></div>
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Email (optionnel)</label>
                      <input type="email" name="client_email" value={formData.client_email} onChange={handleChange} className="input-luxury w-full bg-transparent" placeholder="email@client.com" data-testid="input-client-email" /></div>
                  </div>

                  {/* Ordered by (operator selector) */}
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Commandé par</label>
                    <div className="flex gap-2">
                      <select value={selectedOperator} onChange={(e) => setSelectedOperator(e.target.value)}
                        className="input-luxury flex-1 bg-transparent text-sm" data-testid="select-operator">
                        <option value="">— Sélectionner ou saisir —</option>
                        {operators.map(o => <option key={o.id} value={o.name}>{o.name}{o.role ? ` (${o.role})` : ""}</option>)}
                      </select>
                      <input type="text" value={selectedOperator} onChange={(e) => setSelectedOperator(e.target.value)}
                        className="input-luxury flex-1 bg-transparent text-sm" placeholder="Ou tapez un nom..." data-testid="input-operator-name" />
                    </div>
                    {operators.length === 0 && <p className="text-[#1A1A1A]/40 text-xs mt-1">Ajoutez des membres dans l{"'"}onglet Équipe</p>}
                  </div>

                  {/* Vehicle */}
                  <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Véhicule *</label>
                    <Select value={formData.vehicle_type} onValueChange={(v) => setFormData(p => ({ ...p, vehicle_type: v }))}>
                      <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]" data-testid="select-vehicle"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-[#1A1A1A]/10">
                        {bookableVehicles.map(v => <SelectItem key={v.id} value={v.id} className="text-[#1A1A1A] hover:bg-[#EDEBE4]">{v.name} — {v.capacity} pass. max</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {formData.vehicle_type && (
                      <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="mt-3 overflow-hidden rounded-sm border border-[#1A1A1A]/10">
                        <img src={bookableVehicles.find(v => v.id === formData.vehicle_type)?.image} alt="" className="w-full h-32 object-cover" />
                      </motion.div>
                    )}
                  </div>

                  {/* Duration (hourly) */}
                  {missionType === "hourly" && (
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Durée *</label>
                      <Select value={hourlyDuration} onValueChange={setHourlyDuration}>
                        <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-[#1A1A1A]/10">
                          {[{ id: "hourly_3h", label: `3h — ${vehiclePrices[formData.vehicle_type]?.hourly_3h || "—"}€` }, { id: "hourly_6h", label: `6h — ${vehiclePrices[formData.vehicle_type]?.hourly_6h || "—"}€` }, { id: "hourly_10h", label: `10h — ${vehiclePrices[formData.vehicle_type]?.hourly_10h || "—"}€` }].map(d => (
                            <SelectItem key={d.id} value={d.id} className="text-[#1A1A1A] hover:bg-[#EDEBE4]">{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Date & Time */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Date</label>
                      <Popover><PopoverTrigger asChild>
                        <button type="button" className="input-luxury w-full bg-transparent text-left flex items-center justify-between" data-testid="input-date">
                          <span className={date ? "text-[#1A1A1A]" : "text-[#1A1A1A]/50"}>{date ? format(date, "dd MMMM yyyy", { locale: fr }) : "Sélectionner une date"}</span>
                          <CalendarIcon size={16} className="text-[#1A1A1A]/50" />
                        </button></PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-white border-[#1A1A1A]/10" align="start"><Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date()} initialFocus /></PopoverContent>
                      </Popover>
                    </div>
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Heure</label>
                      <Select value={formData.pickup_time} onValueChange={(v) => setFormData(p => ({ ...p, pickup_time: v }))}>
                        <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-[#1A1A1A]/10 max-h-60">{timeSlots.map(tm => <SelectItem key={tm} value={tm} className="text-[#1A1A1A] hover:bg-[#EDEBE4]">{tm}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Addresses */}
                  <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2"><MapPin size={14} className="inline mr-1" /> Adresse de prise en charge</label>
                    <AddressAutocomplete value={formData.pickup_address} onChange={(v) => setFormData(p => ({...p, pickup_address: v}))} onPlaceSelect={onPickupPlaceSelect} placeholder="Adresse, hôtel, aéroport..." testId="input-pickup" />
                  </div>
                  {missionType === "simple" && (
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2"><MapPin size={14} className="inline mr-1" /> Destination</label>
                      <AddressAutocomplete value={formData.dropoff_address} onChange={(v) => setFormData(p => ({...p, dropoff_address: v}))} onPlaceSelect={onDropoffPlaceSelect} placeholder="Destination..." testId="input-dropoff" />
                    </div>
                  )}

                  {/* Detected package */}
                  {missionType === "simple" && detectedPkg && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                      className={`p-4 border ${detectedPkg.type === "km" ? "border-[#1A1A1A]/20 bg-[#EDEBE4]" : "border-[#1A1A1A]/30 bg-[#1A1A1A]/5"}`}>
                      <div className="flex items-start gap-3">
                        {DetectedIcon && <DetectedIcon size={22} weight="bold" className="text-[#1A1A1A] mt-0.5 flex-shrink-0" />}
                        <div className="flex-1">
                          <p className="text-[#1A1A1A] text-sm font-medium">{DETECTION_LABELS[detectedPkg.type]}</p>
                          {detectedPkg.type === "km" ? (
                            <p className="text-[#1A1A1A]/60 text-xs mt-1">
                              {calculatingDistance ? "Calcul de la distance..." : distanceKm ? `${distanceKm} km · ${kmRates[formData.vehicle_type]?.rate}€/km` : `${kmRates[formData.vehicle_type]?.rate}€/km`}
                            </p>
                          ) : (
                            <p className="text-[#1A1A1A]/60 text-xs mt-1">{vehiclePrices[formData.vehicle_type]?.[detectedPkg.id]}€</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Flight/Train number */}
                  {missionType === "simple" && isAirportOrStation && (
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">
                      {detectedPkg.hasAirport ? <><AirplaneTakeoff size={14} className="inline mr-1" /> N° de vol</> : <><Train size={14} className="inline mr-1" /> N° de train</>}
                    </label>
                      <input type="text" name="flight_train_number" value={formData.flight_train_number} onChange={handleChange} className="input-luxury w-full bg-transparent" placeholder={detectedPkg.hasAirport ? "Ex: AF1213" : "Ex: TGV 6201"} data-testid="input-flight" />
                    </div>
                  )}

                  {/* Passengers & Luggage */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2"><Users size={14} className="inline mr-1" /> Passagers</label>
                      <Select value={formData.passengers.toString()} onValueChange={(v) => setFormData(p => ({ ...p, passengers: parseInt(v) }))}>
                        <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-[#1A1A1A]/10">{[1,2,3,4,5,6,7].map(n => <SelectItem key={n} value={n.toString()} className="text-[#1A1A1A] hover:bg-[#EDEBE4]">{n} passager{n > 1 ? "s" : ""}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2"><Briefcase size={14} className="inline mr-1" /> Bagages</label>
                      <Select value={formData.luggage.toString()} onValueChange={(v) => setFormData(p => ({ ...p, luggage: parseInt(v) }))}>
                        <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-[#1A1A1A]/10">{[0,1,2,3,4,5,6,7,8].map(n => <SelectItem key={n} value={n.toString()} className="text-[#1A1A1A] hover:bg-[#EDEBE4]">{n} bagage{n > 1 ? "s" : ""}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Notes */}
                  <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Notes</label>
                    <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} className="input-luxury w-full bg-transparent resize-none" placeholder="VIP, siège enfant, Meet & Greet..." data-testid="input-notes" /></div>

                  {/* Internal Notes (Partner ↔ Admin) */}
                  <div className="bg-amber-50/50 border border-amber-200/50 p-4">
                    <label className="text-amber-800/70 text-xs tracking-wide uppercase flex items-center gap-1.5 mb-2">
                      <ChatText size={14} /> Commentaires internes
                    </label>
                    <textarea name="internal_notes" value={formData.internal_notes} onChange={handleChange} rows={2}
                      className="input-luxury w-full bg-transparent resize-none text-sm" placeholder="N° chambre, consignes, bagages spéciaux... (visible uniquement par vous et Altura)" data-testid="input-internal-notes" />
                    <p className="text-amber-600/60 text-[10px] mt-1">Non visible par le client</p>
                  </div>

                  {/* Extra Stops */}
                  {missionType === "simple" && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase">Arrêts supplémentaires</label>
                        <button type="button" onClick={() => setExtraStops(prev => [...prev, { name: "", address: "", notes: "", order: prev.length + 1 }])}
                          className="text-xs px-3 py-1.5 border border-[#1A1A1A]/20 hover:bg-[#EDEBE4] transition-colors flex items-center gap-1.5" data-testid="add-extra-stop-btn">
                          <Plus size={12} /> Ajouter un arrêt
                        </button>
                      </div>
                      {extraStops.length > 0 && (
                        <div className="space-y-3">
                          {extraStops.map((stop, idx) => (
                            <motion.div key={`stop-${idx}-${stop.name || stop.address}`} initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                              className="bg-[#EDEBE4]/50 border border-[#1A1A1A]/10 p-4">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-[#1A1A1A]/60 text-xs font-medium">Arrêt {idx + 1}</p>
                                <button type="button" onClick={() => setExtraStops(prev => prev.filter((_, i) => i !== idx))}
                                  className="text-red-400 hover:text-red-600 text-xs">Retirer</button>
                              </div>
                              <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-[#1A1A1A]/50 text-[10px] uppercase tracking-wide block mb-1">Nom du lieu</label>
                                  <input type="text" value={stop.name} onChange={(e) => { const v = e.target.value; setExtraStops(prev => prev.map((s, i) => i === idx ? { ...s, name: v } : s)); }}
                                    className="input-luxury w-full bg-transparent text-sm" placeholder="Hôtel Meurice, Ritz..." data-testid={`extra-stop-name-${idx}`} />
                                </div>
                                <div>
                                  <label className="text-[#1A1A1A]/50 text-[10px] uppercase tracking-wide block mb-1">Adresse</label>
                                  <input type="text" value={stop.address} onChange={(e) => { const v = e.target.value; setExtraStops(prev => prev.map((s, i) => i === idx ? { ...s, address: v } : s)); }}
                                    className="input-luxury w-full bg-transparent text-sm" placeholder="228 Rue de Rivoli, Paris" data-testid={`extra-stop-addr-${idx}`} />
                                </div>
                              </div>
                              <div className="mt-2">
                                <label className="text-[#1A1A1A]/50 text-[10px] uppercase tracking-wide block mb-1">Note</label>
                                <input type="text" value={stop.notes} onChange={(e) => { const v = e.target.value; setExtraStops(prev => prev.map((s, i) => i === idx ? { ...s, notes: v } : s)); }}
                                  className="input-luxury w-full bg-transparent text-sm" placeholder="Récupérer un passager..." data-testid={`extra-stop-note-${idx}`} />
                              </div>
                            </motion.div>
                          ))}
                          <p className="text-[#1A1A1A]/40 text-xs">
                            +{(() => { const hr = { classe_e: 70, classe_s: 100, classe_v: 80 }; return (hr[formData.vehicle_type] || 70) * extraStops.length; })()}€ ({extraStops.length} arrêt{extraStops.length > 1 ? "s" : ""} × {({ classe_e: 70, classe_s: 100, classe_v: 80 })[formData.vehicle_type] || 70}€/h) · Commission : +{extraStops.length * 10}€
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment Method */}
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-3">Mode de règlement client *</label>
                    <div className="grid grid-cols-2 gap-0">
                      <button type="button" onClick={() => setPaymentMethod("online")}
                        className={`p-4 border text-left transition-all ${paymentMethod === "online" ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A]"}`} data-testid="payment-online-btn">
                        <div className="flex items-center gap-3 mb-1"><CreditCard size={20} weight={paymentMethod === "online" ? "bold" : "light"} /><span className="text-sm font-medium">Paiement en ligne</span></div>
                        <p className={`text-xs ${paymentMethod === "online" ? "text-white/70" : "text-[#1A1A1A]/50"}`}>Lien Stripe envoyé au client</p>
                      </button>
                      <button type="button" onClick={() => setPaymentMethod("onboard")}
                        className={`p-4 border border-l-0 text-left transition-all ${paymentMethod === "onboard" ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 text-[#1A1A1A]"}`} data-testid="payment-onboard-btn">
                        <div className="flex items-center gap-3 mb-1"><Money size={20} weight={paymentMethod === "onboard" ? "bold" : "light"} /><span className="text-sm font-medium">Règlement à bord</span></div>
                        <p className={`text-xs ${paymentMethod === "onboard" ? "text-white/70" : "text-[#1A1A1A]/50"}`}>Client règle au chauffeur</p>
                      </button>
                    </div>
                  </div>

                  {/* Pricing Section */}
                  <div className="border-t border-[#1A1A1A]/10 pt-6 mt-4 space-y-5">
                    <div className="flex justify-between items-center">
                      <span className="text-[#1A1A1A]/70 text-sm">Tarif de base ALTURA</span>
                      <span className="font-heading text-2xl text-[#1A1A1A]" data-testid="base-price">{total.toFixed(2)}€</span>
                    </div>

                    {total > 0 && (
                      <div>
                        <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Votre prix client (optionnel — minimum {total.toFixed(0)}€)</label>
                        <div className="relative">
                          <input type="number" min={total} step="1" value={customPrice} onChange={(e) => setCustomPrice(e.target.value)}
                            className="input-luxury w-full bg-transparent pr-8" placeholder={`${total.toFixed(0)}€ minimum`} data-testid="input-custom-price" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40 text-sm">€</span>
                        </div>
                      </div>
                    )}

                    {commissionPreview && total > 0 && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-[#EDEBE4] border border-[#1A1A1A]/10 p-5 space-y-3" data-testid="commission-preview">
                        <p className="text-[#1A1A1A]/60 text-xs tracking-wide uppercase mb-2">Aperçu de votre commission</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-[#1A1A1A]/60">Tarif de base ALTURA</span><span className="text-[#1A1A1A]">{commissionPreview.base_price}€</span></div>
                          {commissionPreview.surplus > 0 && (<>
                            <div className="flex justify-between"><span className="text-[#1A1A1A]/60">Prix client</span><span className="text-[#1A1A1A] font-medium">{commissionPreview.final_price}€</span></div>
                            <div className="flex justify-between text-[#1A1A1A]/40 text-xs"><span>Surplus ({commissionPreview.surplus}€ × 50%)</span><span>+{commissionPreview.surplus_partner}€</span></div>
                          </>)}
                          <div className="border-t border-[#1A1A1A]/10 pt-2 mt-2">
                            <div className="flex justify-between"><span className="text-[#1A1A1A]/60">Commission de base</span><span className="text-[#1A1A1A]">{commissionPreview.base_commission}€</span></div>
                            {commissionPreview.surplus_partner > 0 && (
                              <div className="flex justify-between"><span className="text-[#1A1A1A]/60">Bonus surplus (50%)</span><span className="text-green-700">+{commissionPreview.surplus_partner}€</span></div>
                            )}
                          </div>
                          <div className="border-t border-[#1A1A1A]/15 pt-3 mt-2 flex justify-between">
                            <span className="text-[#1A1A1A] font-medium">Votre commission totale</span>
                            <span className="font-heading text-xl text-green-700">{commissionPreview.total_commission}€</span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <div className="flex justify-between items-center pt-2">
                      <span className="text-[#1A1A1A] font-medium">Le client paie</span>
                      <span className="font-heading text-3xl text-[#1A1A1A]" data-testid="total-amount">
                        {(customPrice && parseFloat(customPrice) >= total ? parseFloat(customPrice) : total).toFixed(2)}€
                      </span>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-3">
                      <button type="submit" disabled={loading}
                        className="btn-dark flex-1 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-booking">
                        {loading ? "Création..." : paymentMethod === "onboard" ? "Confirmer la commande" : "Confirmer et générer le lien de paiement"}
                        <ArrowRight size={16} weight="bold" />
                      </button>
                      <button type="button" onClick={(e) => handleSubmit(e, true)} disabled={loading}
                        className="px-5 py-3 border border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:bg-[#EDEBE4] transition-colors text-xs flex items-center gap-2 disabled:opacity-50"
                        data-testid="save-draft-btn">
                        <FloppyDisk size={16} /> Brouillon
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </>
        )}

        {/* ═══ HISTORY TAB ═══ */}
        {tab === "history" && dashboard && (
          <HistoryTab dashboard={dashboard} onEdit={loadDraftIntoForm} onConfirm={confirmDraft} onDeleteDraft={deleteDraft} />
        )}

        {/* ═══ COMMISSIONS TAB ═══ */}
        {tab === "commissions" && dashboard && (
          <div>
            <h2 className="font-heading text-xl text-[#1A1A1A] mb-6">Mes commissions</h2>
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-white border border-[#1A1A1A]/10 p-5 text-center">
                <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">En attente</p>
                <p className="font-heading text-2xl text-[#1A1A1A]">{dashboard.commissions.pending}€</p>
                <p className="text-[#1A1A1A]/40 text-xs mt-1">Courses non terminées</p>
              </div>
              <div className="bg-white border border-[#1A1A1A]/10 p-5 text-center">
                <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Disponible</p>
                <p className="font-heading text-2xl text-green-700">{dashboard.commissions.available}€</p>
                <p className="text-[#1A1A1A]/40 text-xs mt-1">Versement prochain mardi</p>
              </div>
              <div className="bg-white border border-[#1A1A1A]/10 p-5 text-center">
                <p className="text-[#1A1A1A]/50 text-xs uppercase tracking-wide mb-1">Total payé</p>
                <p className="font-heading text-2xl text-[#1A1A1A]">{dashboard.commissions.paid}€</p>
                <p className="text-[#1A1A1A]/40 text-xs mt-1">Historique des versements</p>
              </div>
            </div>

            {/* Detailed commission per booking */}
            {dashboard.commission_details && dashboard.commission_details.length > 0 && (
              <div className="mb-8">
                <h3 className="font-heading text-lg text-[#1A1A1A] mb-4">Détail par course</h3>
                <div className="space-y-2">
                  {dashboard.commission_details.map(c => (
                    <div key={c.id} className="bg-white border border-[#1A1A1A]/10 p-4 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-[#1A1A1A] text-sm font-medium truncate">{c.client_name}</p>
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-sm flex-shrink-0 ${
                            c.commission_status === "paid" ? "bg-green-100 text-green-800" :
                            c.commission_status === "available" ? "bg-blue-100 text-blue-800" :
                            c.commission_status === "cancelled" ? "bg-red-100 text-red-800" :
                            "bg-yellow-100 text-yellow-800"
                          }`}>
                            {c.commission_status === "paid" ? "Payé" : c.commission_status === "available" ? "Disponible" : c.commission_status === "cancelled" ? "Annulé" : "En attente"}
                          </span>
                        </div>
                        <p className="text-[#1A1A1A]/50 text-xs">
                          {c.pickup_date} · {VEHICLE_NAMES[c.vehicle_type] || c.vehicle_type} · Client: {c.total_amount}€
                        </p>
                        <div className="text-[#1A1A1A]/40 text-xs mt-1 flex flex-wrap gap-3">
                          <span>Base : {c.commission_base ?? 0}€</span>
                          {(c.commission_surplus ?? 0) > 0 && <span>Surplus 50% : +{c.commission_surplus}€</span>}
                          {c.extra_stops_commission > 0 && <span>Arrêts sup. : +{c.extra_stops_commission}€</span>}
                          {c.commission_paid_at && <span>Payé le {c.commission_paid_at.substring(0, 10)}</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`font-heading text-xl ${c.commission_status === "cancelled" ? "text-red-400 line-through" : "text-green-700"}`}>
                          {c.commission_amount}€
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(!dashboard.commission_details || dashboard.commission_details.length === 0) && (
              <p className="text-[#1A1A1A]/50 text-sm text-center py-8 mb-8">Aucune commission pour le moment</p>
            )}

            <PayoutPreferences />
          </div>
        )}

        {/* ═══ TEAM TAB ═══ */}
        {tab === "team" && (
          <TeamTab operators={operators} onReload={loadDashboard} />
        )}

        {/* ═══ PROFILE TAB ═══ */}
        {tab === "profile" && (
          <ProfileTab />
        )}
      </div>
    </main>
  );
};

export default PartnerDashboard;
